import React from 'react'
import { useSelector } from 'react-redux'

import { selectOrgId } from '@/app/module/user/store/selectors'
import { getExt } from '@/app/service/util/file'
import type { FilesType, FileType } from '@/app/types'

type FileUpdate = Pick<FileType, 'filename'> &
  Partial<Pick<FileType, 'url' | 'title' | 'transcription'>> & { id: string }
type CampaignFilesValue = {
  audioFiles: FilesType
  files: { data: FilesType; loadingItems: string[] }
  finishLoading: (id: string) => void
  removeFile: (filename: string) => void
  saveFile: (file: FileType & { id: string }) => void
  setFiles: (files: FilesType) => void
  startLoading: (id: string) => void
  updateFile: (file: FileUpdate) => void
}

type Props = React.PropsWithChildren<{
  campaignId?: number | string
  initialFiles?: FilesType
}>

const CampaignFilesContext = React.createContext<CampaignFilesValue | undefined>(undefined)

const CampaignFilesProvider: React.FC<Props> = ({ campaignId, children, initialFiles }) => {
  const orgId = useSelector(selectOrgId)
  const [state, setState] = React.useState(() => ({
    scope: { campaignId, orgId },
    initialFiles,
    files: { data: { ...initialFiles }, loadingItems: [] as string[] },
  }))
  const { files, scope } = state

  // Reset before children render, without mutating the committed scope during suspended renders.
  if (scope.campaignId !== campaignId || scope.orgId !== orgId) {
    setState({
      scope: { campaignId, orgId },
      initialFiles,
      files: { data: { ...initialFiles }, loadingItems: [] },
    })
  } else if (state.initialFiles !== initialFiles) {
    setState({ ...state, initialFiles, files: { ...files, data: mergeFiles(files.data, initialFiles) } })
  }

  const actions = React.useMemo(() => {
    const commit = (update: (files: CampaignFilesValue['files']) => CampaignFilesValue['files']) => {
      setState((current) => (current.scope === scope ? { ...current, files: update(current.files) } : current))
    }
    return {
      finishLoading: (id: string) => {
        commit((current) => ({ ...current, loadingItems: current.loadingItems.filter((item) => item !== id) }))
      },
      removeFile: (filename: string) => {
        commit((current) => {
          const data = { ...current.data }
          delete data[filename]
          return { ...current, data }
        })
      },
      saveFile: (file: FileType & { id: string }) => {
        commit((current) => ({
          data: { ...current.data, [file.filename]: file },
          loadingItems: current.loadingItems.filter((id) => id !== file.id),
        }))
      },
      setFiles: (files: FilesType) => {
        commit((current) => ({ ...current, data: mergeFiles(current.data, files) }))
      },
      startLoading: (id: string) => {
        commit((current) => ({
          ...current,
          loadingItems: current.loadingItems.includes(id) ? current.loadingItems : [...current.loadingItems, id],
        }))
      },
      updateFile: (file: FileUpdate) => {
        commit((current) => ({
          data: { ...current.data, [file.filename]: { ...current.data[file.filename], ...file } },
          loadingItems: current.loadingItems.filter((id) => id !== file.id),
        }))
      },
    }
  }, [scope])
  const audioFiles = React.useMemo(
    () =>
      Object.fromEntries(
        Object.entries(files.data).filter(([filename]) => ['wav', 'mp3', 'ogg'].includes(getExt(filename))),
      ),
    [files.data],
  )
  const value = React.useMemo(() => ({ audioFiles, files, ...actions }), [actions, audioFiles, files])

  return <CampaignFilesContext.Provider value={value}>{children}</CampaignFilesContext.Provider>
}

const mergeFiles = (existing: FilesType, incoming?: FilesType): FilesType => {
  const files = { ...existing }
  Object.entries(incoming ?? {}).forEach(([filename, file]) => {
    files[filename] = { ...files[filename], ...file }
  })
  return files
}

export const useCampaignFiles = () => {
  const context = React.useContext(CampaignFilesContext)
  if (!context) throw new Error('useCampaignFiles must be used within a CampaignFilesProvider')
  return context
}

const emptyAudioFiles: FilesType = {}

// The shared audio picker is also rendered outside the campaign editor (for example, in conversations).
export const useCampaignAudioFiles = () => React.useContext(CampaignFilesContext)?.audioFiles ?? emptyAudioFiles

export default CampaignFilesProvider
